import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { DocumentIcon } from "@common/documentIcon";
import { UploadIcon } from "./uploadIcon";
import { CreateFolderIcon } from "./createFolderIcon";
import { Props } from "./types";
import styles from "./styles.scss";
import { LockIcon } from "@common/lockIcon";
import classNames from "classnames";
import { RestrictedArea } from "@scenes/restrictedArea";

function CreationMenu({ folder }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { pushView } = useModal();

  return (
    <Safeguard
      useCases={[
        UseCases.uploadDocument,
        UseCases.createFolder,
        UseCases.createDocument,
      ]}
    >
      {({ canCreateFolder, canUploadDocument, canCreateDocument }) =>
        (canUploadDocument || canCreateFolder) && (
          <VisibilityWatcher className={styles.creationMenu}>
            {(isVisible, show, hide) => (
              <React.Fragment>
                <button
                  onClick={isVisible ? hide : show}
                  className={styles.button}
                >
                  <div className={styles.iconBox}>+</div>
                  <p>
                    {t(
                      "documentsPage.documentsPage.elementCreationMenu.button",
                    )}
                  </p>
                </button>
                {isVisible && (
                  <div className={styles.menu}>
                    {canUploadDocument && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.uploadDocument,
                            data: {
                              namespace: planzone.uuid,
                              type: "planzone",
                              folder,
                            },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <UploadIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t(
                            "documentsPage.documentsPage.elementCreationMenu.uploadDocument",
                          )}
                        </p>
                      </div>
                    )}
                    {canCreateFolder && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createFolder,
                            data: {
                              namespace: planzone.uuid,
                              type: "planzone",
                              parentFolder: folder,
                              rootName: planzone.name,
                            },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <CreateFolderIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t(
                            "documentsPage.documentsPage.elementCreationMenu.createFolder",
                          )}
                        </p>
                      </div>
                    )}
                    <RestrictedArea areas={["onlyOffice"]}>
                      {({ canAccessOnlyOffice }) => {
                        const canUseOnlyOffice =
                          settings.canUseOnlyOffice && canAccessOnlyOffice;

                        return (
                          <div>
                            {canCreateDocument && (
                              <div>
                                <div
                                  className={classNames(styles.menuItem, {
                                    [styles.block]: !canUseOnlyOffice,
                                  })}
                                  onClick={() =>
                                    canUseOnlyOffice &&
                                    pushView({
                                      key: UseCases.createDocument,
                                      data: {
                                        documentType: ".docx",
                                        namespace: planzone.uuid,
                                        type: "planzone",
                                        projectName: planzone.name,
                                      },
                                    })
                                  }
                                >
                                  <div className={styles.fileIcon}>
                                    {!canUseOnlyOffice ? (
                                      <LockIcon className={styles.lockIcon} />
                                    ) : (
                                      <DocumentIcon
                                        mimeType={"application/msword"}
                                      />
                                    )}
                                  </div>
                                  <p className={styles.menuText}>
                                    {t(
                                      "documentsPage.documentsPage.elementCreationMenu.createDocx",
                                    )}
                                  </p>
                                </div>
                                <div
                                  className={classNames(styles.menuItem, {
                                    [styles.block]: !canUseOnlyOffice,
                                  })}
                                  onClick={() =>
                                    canUseOnlyOffice &&
                                    pushView({
                                      key: UseCases.createDocument,
                                      data: {
                                        documentType: ".pptx",
                                        namespace: planzone.uuid,
                                        type: "planzone",
                                        projectName: planzone.name,
                                      },
                                    })
                                  }
                                >
                                  <div className={styles.fileIcon}>
                                    {!canUseOnlyOffice ? (
                                      <LockIcon className={styles.lockIcon} />
                                    ) : (
                                      <DocumentIcon
                                        mimeType={
                                          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                        }
                                      />
                                    )}
                                  </div>
                                  <p className={styles.menuText}>
                                    {t(
                                      "documentsPage.documentsPage.elementCreationMenu.createSlide",
                                    )}
                                  </p>
                                </div>
                                <div
                                  className={classNames(styles.menuItem, {
                                    [styles.block]: !canUseOnlyOffice,
                                  })}
                                  onClick={() =>
                                    canUseOnlyOffice &&
                                    pushView({
                                      key: UseCases.createDocument,
                                      data: {
                                        documentType: ".xlsx",
                                        namespace: planzone.uuid,
                                        type: "planzone",
                                        projectName: planzone.name,
                                      },
                                    })
                                  }
                                >
                                  <div className={styles.fileIcon}>
                                    {!canUseOnlyOffice ? (
                                      <LockIcon className={styles.lockIcon} />
                                    ) : (
                                      <DocumentIcon mimeType={"image/xls"} />
                                    )}
                                  </div>
                                  <p className={styles.menuText}>
                                    {t(
                                      "documentsPage.documentsPage.elementCreationMenu.createExcel",
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </RestrictedArea>
                  </div>
                )}
              </React.Fragment>
            )}
          </VisibilityWatcher>
        )
      }
    </Safeguard>
  );
}

export { CreationMenu };
